import { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { KstDataGrid } from '@/components/KstDataGrid';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const month = (date.getMonth() + 1);
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return (month < 10 ? '0' : '') + month + '/' +
         (day < 10 ? '0' : '') + day + '/' +
         year + ' ' +
         hours + ':' +
         (minutes < 10 ? '0' : '') + minutes + ':' +
         (seconds < 10 ? '0' : '') + seconds + ' ' + ampm;
}

function getActionClass(action: string): string {
  switch (action) {
    case 'Edits - Created': return 'audit-action-create';
    case 'Edits - Deleted': return 'audit-action-delete';
    case 'Edits - Modified': return 'audit-action-update';
    case 'Edits - Permission Added': return 'audit-action-create';
    case 'Edits - Permission Removed': return 'audit-action-delete';
    case 'Edits - Person Added': return 'audit-action-create';
    case 'Edits - Person Removed': return 'audit-action-delete';
    default: return '';
  }
}

function getActionLabel(action: string): string {
  switch (action) {
    case 'Edits - Created': return 'Created';
    case 'Edits - Deleted': return 'Deleted';
    case 'Edits - Modified': return 'Modified';
    case 'Edits - Permission Added': return 'Perm Added';
    case 'Edits - Permission Removed': return 'Perm Removed';
    case 'Edits - Person Added': return 'User Added';
    case 'Edits - Person Removed': return 'User Removed';
    default: return action;
  }
}

function getAuditLogGridColumns(): KstDataGridColumn[] {
  return [
    {
      field: 'formattedDateTime',
      name: 'Date/Time',
      size: '160px',
      template: (e: any) => formatDateTime(e.dateCreated),
    },
    { field: 'loggedByFirstName', name: 'First', size: '100px' },
    { field: 'loggedByLastName', name: 'Last', size: '100px' },
    {
      field: 'action',
      name: 'Action',
      size: '120px',
      template: (e: any) =>
        `<span class="audit-action-badge ${getActionClass(e.action)}">${getActionLabel(e.action)}</span>`,
    },
    {
      field: 'entry',
      name: 'Entry',
      template: (e: any) => e.entry || '',
    },
  ];
}

interface PermissionsAuditLogProps {
  isVisible: boolean;
}

export const PermissionsAuditLog = memo(function PermissionsAuditLog({
  isVisible,
}: PermissionsAuditLogProps) {
  const [loading, setLoading] = useState(false);
  const [gridRows, setGridRows] = useState<KstDataGridRows>({ entries: null });
  const [gridPager, setGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    pageSize: 20,
    isVisible: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(1);
  const hasLoadedRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const columns = useMemo(() => getAuditLogGridColumns(), []);

  const loadAuditLog = useCallback((pageOverride?: number) => {
    setLoading(true);
    const page =
      pageOverride !== undefined ? pageOverride : currentPageRef.current;

    const requestData = {
      data: {
        page: page,
        pageSize: pageSize,
      },
    };

    api.getPermissionAuditLog(
      requestData,
      (result: any) => {
        const newTotalCount = result.records || 0;
        const newTotalPages = result.numPages || Math.ceil(newTotalCount / pageSize) || 1;
        const entries = result.logs || [];

        setTotalPages(newTotalPages);
        setGridRows({
          entries: entries.map((e: any) => ({
            ...e,
            formattedDateTime: formatDateTime(e.dateCreated),
          })),
        });
        setGridPager({
          curPage: page,
          numPages: newTotalPages,
          records: newTotalCount,
          pageSize: pageSize,
          isVisible: newTotalPages > 1,
        });
        setLoading(false);
      },
      () => {
        setGridRows({ entries: [] });
        setGridPager({
          curPage: 1,
          numPages: 1,
          records: 0,
          pageSize: pageSize,
          isVisible: false,
        });
        setLoading(false);
        App.showPopupNotification('Failed to load audit log', 'error');
      }
    );
  }, []);

  useEffect(() => {
    if (isVisible && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAuditLog();
    }
  }, [isVisible, loadAuditLog]);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
      loadAuditLog(page);
    },
    [totalPages, loadAuditLog]
  );

  return (
    <div style={{ display: isVisible ? undefined : 'none' }}>
      <KstDataGrid
        columns={columns}
        rows={gridRows}
        pager={gridPager}
        showcheckbox={false}
        title="Permission Group Audit Log"
        mobiletitlefield="formattedDateTime"
        loading={loading}
        onGoToPage={goToPage}
      />
    </div>
  );
});
